import { Button, Card, Col, Flex, Row, Space, Table, Tag, Spin, Typography } from "antd";
import React, { useState } from "react";
import Title from "antd/es/typography/Title";
import { useEffect } from "react";
import { DeleteIcon, EditIcon } from "../../../../../../assets/svg";
import { colors } from "../../../../../../constants/colors";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../../common/spinner/Spinner";
import styles from "./DiscountCodes.module.scss"
import { dateObjToDispViewDate,timeObjToDispViewTimeWithSuffix } from "../../../../../../utils/utils";
import { baseRouteMaker, baseRouteMakerForSuperAdmin, routes } from "../../../../../../constants/routeConsts";
import { getGiftCardsAction } from "../../../../../../redux/services/gift-card/giftCardServices";
import Actions from "../../../../../../redux/actions";
import DiscountCodesDeleteDialog from "./DiscountCodesDeleteDialog";
import useDebounce from "../../../../../../hooks/useDebounce";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { TagOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.guess();

const DiscountCodes = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* dialog dependencies */
  const [openGiftCardDeleteDialog, setOpenGiftCardDeleteDialog] = useState(false);
  /* dialog dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const {giftCards} = useSelector((state)=>state.giftCards)
  const [searchValue, setSearchValue] = useState("");
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: giftCards?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */


  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
    }
    dispatch(getGiftCardsAction(dataToBeSent, setLoading))
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title:  t("Modules.DiscountCodes.DiscountCodesForm.giftCardName"),
        dataIndex: "gift_card_name",
        key: "gift_card_name"
      },
      {
        title: t("Modules.WalletInfo.amount"),
        dataIndex: "amount",
        key: "amount",
      },
      {
        title: t("Modules.DiscountCodes.DiscountCodesForm.walletPoints"),
        dataIndex: "wallet_points",
        key: "wallet_points",
      },
      {
        title: t("Modules.DiscountCodes.DiscountCodesForm.startDate"),
        dataIndex: "start",
        key: "start",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.start, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("Modules.DiscountCodes.DiscountCodesForm.expiryDate"),
        dataIndex: "expiry",
        key: "expiry",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.expiry, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("useCount"),
        dataIndex: "useCount",
        key: "useCount",
        render: (_, record) => (
          <p>{record?.use_count || 0}</p>
        ),
      },
      {
        title: t("cardStatus"),
        key: "is_active",
        dataIndex: "is_active",
        render: (_, record) => {
          const getColorBasedOnStatus = (status) => {
            switch (status) {
              case true:
                return { background: colors.lightTeal, color: colors.darkTeal };
  
              case false:
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };
  
              default:
                break;
            }
          };
          return (
            <>
              <Tag
                bordered={false}
                style={{
                  ...getColorBasedOnStatus(record?.is_active),
                  textTransform: "capitalize",
                }}
              >
                {record?.is_active === true ? t("CommonUtils.active") : t("CommonUtils.inactive")}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("createAt"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{`${dateObjToDispViewDate(record?.created_at, "ddmmyyyy")} - ${timeObjToDispViewTimeWithSuffix(dayjs
              .utc(record?.created_at).local().tz(dayjs.tz.guess()).format("YYYY-MM-DDTHH:mm:ss")
          )}`}</p>
        ),
      },
      {
        title: t("appointmentInfo.action"),
        key: "action",
        align:'center',
        render: (_, record) => {
          return (
            <>
            {
              record?.is_active ?
              <Flex gap="small" justify="center">
                <Space size="middle">
                  <Button
                    type="text"
                    size="medium"
                    icon={<EditIcon fillColor={colors.slateGray} />}
                    onClick={(e) => handleAddEditDetails(e, record)}
                  />
                </Space>
                <Space size="middle">
                  <Button
                    type="text"
                    size="medium"
                    icon={<DeleteIcon fillColor={colors.slateGray} />}
                    onClick={(e) => handleConfirmDelete(e, record)}
                  />
                </Space>
              </Flex>
              :
              <>-</>
            }
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleAddEditDetails = (e, row) => {
    navigate(baseRouteMakerForSuperAdmin(routes.ADD_DISCOUNT_CODES))
    dispatch(
      Actions.createAction(Actions.SELECTED_GIFT_CARD_ID, { selectedGiftCardId: "" })
    );
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedGiftCardId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_GIFT_CARD_ID, { selectedGiftCardId })
      );
    }
  };

  const handleConfirmDelete = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_GIFT_CARD_ID, { selectedGiftCardId: "" })
    );
    setOpenGiftCardDeleteDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedGiftCardId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_GIFT_CARD_ID, { selectedGiftCardId })
      );
    }
  };


  return (
    <div className={styles.discountCodesContainer}>
        <Flex vertical gap="2rem">
          <Flex align="center" justify="space-between">
            <Card
                  bordered
                  hoverable
                  style={{
                    width: 300,
                    padding: 0,
                    borderRadius: 20,
                    border: `1px solid ${colors.oceanBlue}`,
                  }}
                >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={6}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<TagOutlined style={{ fontSize: 22 }} />}
                    style={{ background: colors.oceanBlue }}
                  />
                </Col>
                <Col span={18}>
                  <Flex vertical>
                    <Typography.Title level={3}>
                      {t("SignUp.giftCodes")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.DarkMint }}
                    >
                      {`${giftCards?.total || 0}`}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
          </Card>
            <Button
              type="primary"
              onClick={(e) => handleAddEditDetails(e, null)}
            >
            {t("addGift")}
            </Button>
          </Flex>
          {
            loading ? 
            <Spinner />
            :
            <Table dataSource={
            giftCards?.data
          } columns={columns} pagination = {paginationObj}/>
          }
        </Flex>
        <DiscountCodesDeleteDialog
        open={openGiftCardDeleteDialog}
        onClose={()=>{
          setOpenGiftCardDeleteDialog(false)
          handleGetTableData()
        }}
        />
    </div>
  );
};

export default DiscountCodes;
